import {decryptAES, encryptAES} from '../encryption.util';

import {AsyncStorage} from './async-storage';

const PERSISTENCE_KEY = '@screen_persistence_state';

/**
 * Generic function to persist or retrieve screen parameters.
 */
export const persistScreenParams = async <T extends object>(
  screenName: string,
  params?: T | null,
): Promise<T | null> => {
  try {
    const rawData = await AsyncStorage.getItem(PERSISTENCE_KEY);
    let fullState: any = {};

    if (rawData) {
      try {
        fullState = decryptAES(rawData) || {};
      } catch (error) {
        // If we can't decrypt it, the storage is likely corrupted or using old format. Clear it.
        await AsyncStorage.removeItem(PERSISTENCE_KEY);
        fullState = {};
      }
    }

    const hasParams =
      params && typeof params === 'object' && Object.keys(params).length > 0;

    if (hasParams) {
      const serializableParams = Object.keys(params).reduce((acc: any, key) => {
        const value = (params as any)[key];
        if (
          value !== null &&
          value !== undefined &&
          (typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean' ||
            (typeof value === 'object' &&
              !value._navigation &&
              !Array.isArray(value))) // Keep it simple for now
        ) {
          acc[key] = value;
        }
        return acc;
      }, {});

      if (Object.keys(serializableParams).length > 0) {
        fullState[screenName] = serializableParams;
        const encrypted = encryptAES(fullState);
        if (encrypted) {
          await AsyncStorage.setItem(PERSISTENCE_KEY, encrypted);
        }
      }
      return params;
    } else {
      const recovered = fullState[screenName];
      if (recovered) {
        return recovered;
      }
    }
  } catch (error) {
    // Silently handle errors
  }

  return params || null;
};

/**
 * Function to clear persisted parameters for a specific screen.
 */
export const clearScreenParams = async (screenName: string): Promise<void> => {
  try {
    const rawData = await AsyncStorage.getItem(PERSISTENCE_KEY);
    if (!rawData) return;

    let fullState: any = {};
    try {
      fullState = decryptAES(rawData) || {};
    } catch (error) {
      // If we can't decrypt it, clear everything
      await AsyncStorage.removeItem(PERSISTENCE_KEY);
      return;
    }

    if (fullState[screenName]) {
      delete fullState[screenName];
      const encrypted = encryptAES(fullState);
      if (encrypted) {
        await AsyncStorage.setItem(PERSISTENCE_KEY, encrypted);
      }
    }
  } catch (error) {
    // Silently handle errors
  }
};
